<script setup>
import { computed, shallowRef, watch } from 'vue';
import moment from 'moment';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';
import UiProfileHeader from '@/components/ui/UiProfileHeader.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { titleCase } from '@/utils/format';

// A parent's view of their child's attendance: a month at a time, one row
// per subject, with the month's totals on top.
const props = defineProps({
  // Row from My Children: id, name, grade_level, picture.
  child: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });

const store = useStore();

// Codes stored in student_attendance.attendance.
const STATUSES = {
  1: { short: 'P', label: 'Present', variant: 'green' },
  2: { short: 'L', label: 'Late', variant: 'amber' },
  0: { short: 'A', label: 'Absent', variant: 'red' },
  3: { short: 'E', label: 'Excused', variant: 'blue' },
};
const LEGEND = [1, 2, 0, 3].map((code) => ({ code, ...STATUSES[code] }));

const rows = shallowRef([]);
const loading = shallowRef(false);
const failed = shallowRef('');
const month = shallowRef(moment().startOf('month'));

async function load() {
  if (!props.child) return;
  loading.value = true;
  failed.value = '';
  try {
    const data = await apiRequest(
      'get',
      `/parent-records/getMyChildrenAttendance/${props.child.id}/${store.getters.getFilterSelected}`,
    );
    rows.value = Array.isArray(data) ? data : [];
    // Open on the latest month that has records.
    const dates = recordedDates(rows.value);
    if (dates.length) month.value = moment(dates[dates.length - 1]).startOf('month');
  } catch (e) {
    rows.value = [];
    failed.value = errorMessage(e, 'Could not load the attendance.');
  } finally {
    loading.value = false;
  }
}

watch(open, (isOpen) => {
  if (isOpen) {
    month.value = moment().startOf('month');
    load();
  }
});

function recordedDates(list) {
  const set = new Set();
  list.forEach((r) => Object.keys(r).forEach((k) => /^\d{4}-\d{2}-\d{2}$/.test(k) && r[k] != null && set.add(k)));
  return [...set].sort();
}

// Only the days that have a record this month, so the table stays short.
const days = computed(() => recordedDates(rows.value).filter((d) => moment(d).isSame(month.value, 'month')));
const monthLabel = computed(() => month.value.format('MMMM YYYY'));
const statusAt = (row, day) => (row[day] == null ? null : STATUSES[Number(row[day])]);

const totals = computed(() => {
  const count = { 1: 0, 2: 0, 0: 0, 3: 0 };
  rows.value.forEach((r) => days.value.forEach((d) => r[d] != null && count[Number(r[d])]++));
  const marked = count[1] + count[2] + count[0] + count[3];
  return {
    ...count,
    marked,
    // Late still counts as attended.
    rate: marked ? Math.round(((count[1] + count[2] + count[3]) / marked) * 100) : null,
  };
});
const rowTotal = (row, code) => days.value.filter((d) => row[d] != null && Number(row[d]) === code).length;

function shiftMonth(step) {
  month.value = month.value.clone().add(step, 'month');
}

const photo = computed(() => {
  const name = String(props.child?.picture ?? '');
  return name && !name.startsWith('default')
    ? `${process.env.VUE_APP_SERVER}/enroll-student/view/studentFile/${encodeURIComponent(name)}`
    : '';
});
</script>

<template>
  <UiDialog v-model="open" :title="`${child?.name || 'Your child'}'s attendance`" size="xl" :persistent="false">
    <template #header>
      <UiProfileHeader :name="child?.name || ''" :photo="photo" :subtitle="['Attendance', child?.grade_level].filter(Boolean).join(' · ')" :size="48" />
    </template>

    <div class="ca-toolbar">
      <div class="ca-month">
        <v-btn icon="mdi-chevron-left" size="small" variant="text" aria-label="Previous month" @click="shiftMonth(-1)" />
        <span class="ca-month__label">{{ monthLabel }}</span>
        <v-btn icon="mdi-chevron-right" size="small" variant="text" aria-label="Next month" @click="shiftMonth(1)" />
      </div>
      <div class="ca-legend">
        <span v-for="s in LEGEND" :key="s.code" class="ca-legend__item">
          <span class="ca-cell" :class="`ca-cell--${s.variant}`">{{ s.short }}</span>{{ s.label }}
        </span>
      </div>
    </div>

    <div v-if="loading" class="ca-empty">Loading…</div>
    <UiEmptyState
      v-else-if="failed"
      icon="mdi-cloud-off-outline"
      title="Attendance couldn't be loaded"
      :description="failed"
    />
    <UiEmptyState
      v-else-if="!days.length"
      icon="mdi-calendar-blank-outline"
      :title="`No attendance in ${monthLabel}`"
      description="Records appear here once teachers take attendance. Use the arrows to see other months."
    />
    <template v-else>
      <div class="ca-stats">
        <div class="ca-stat">
          <span class="ca-stat__value">{{ totals.rate }}%</span>
          <span class="ca-stat__label">Attendance rate</span>
        </div>
        <div v-for="s in LEGEND" :key="s.code" class="ca-stat">
          <span class="ca-stat__value">{{ totals[s.code] }}</span>
          <span class="ca-stat__label">{{ s.label }}</span>
        </div>
      </div>

      <div class="ca-grid">
        <table>
          <thead>
            <tr>
              <th class="ca-grid__subject">Subject</th>
              <th v-for="d in days" :key="d" class="ca-grid__day">
                <span>{{ moment(d).format('ddd') }}</span>{{ moment(d).format('D') }}
              </th>
              <th class="ca-grid__total" title="Absent">A</th>
              <th class="ca-grid__total" title="Late">L</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.subject">
              <td class="ca-grid__subject">{{ titleCase(row.subject) }}</td>
              <td v-for="d in days" :key="d" class="ca-grid__day">
                <span
                  v-if="statusAt(row, d)"
                  class="ca-cell"
                  :class="`ca-cell--${statusAt(row, d).variant}`"
                  :title="statusAt(row, d).label"
                >
                  {{ statusAt(row, d).short }}
                </span>
              </td>
              <td class="ca-grid__total">
                <UiBadge v-if="rowTotal(row, 0)" variant="red">{{ rowTotal(row, 0) }}</UiBadge>
                <span v-else class="ca-muted">0</span>
              </td>
              <td class="ca-grid__total">
                <UiBadge v-if="rowTotal(row, 2)" variant="amber">{{ rowTotal(row, 2) }}</UiBadge>
                <span v-else class="ca-muted">0</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Close</v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.ca-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}
.ca-month {
  display: inline-flex;
  align-items: center;
}
.ca-month__label {
  min-width: 130px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
}
.ca-legend {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: var(--muted-foreground);
}
.ca-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.ca-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.ca-stat {
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.ca-stat__value {
  font-size: 20px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.ca-stat__label {
  font-size: 12px;
  color: var(--muted-foreground);
}
.ca-grid {
  max-height: 50vh;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.ca-grid table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.ca-grid th,
.ca-grid td {
  padding: 6px 8px;
  border-bottom: 1px solid var(--border);
  background: var(--card);
  white-space: nowrap;
}
.ca-grid thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 500;
  color: var(--muted-foreground);
}
.ca-grid tbody tr:last-child td {
  border-bottom: 0;
}
.ca-grid__subject {
  position: sticky;
  left: 0;
  z-index: 2;
  min-width: 160px;
  text-align: left;
  box-shadow: 1px 0 0 var(--border);
}
.ca-grid thead .ca-grid__subject {
  z-index: 3;
}
.ca-grid__day,
.ca-grid__total {
  text-align: center;
}
.ca-grid__day span {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
}
.ca-muted {
  color: var(--muted-foreground);
}
.ca-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}
/* Same palette as UiBadge, including its dark-mode colours */
.ca-cell--green { background: #ecfdf5; color: #047857; }
.ca-cell--amber { background: #fffbeb; color: #b45309; }
.ca-cell--red { background: #fef2f2; color: #b91c1c; }
.ca-cell--blue { background: #eff6ff; color: #1d4ed8; }
html.dark .ca-cell--green { background: rgba(16, 185, 129, 0.15); color: #6ee7b7; }
html.dark .ca-cell--amber { background: rgba(245, 158, 11, 0.15); color: #fcd34d; }
html.dark .ca-cell--red { background: rgba(239, 68, 68, 0.15); color: #fca5a5; }
html.dark .ca-cell--blue { background: rgba(59, 130, 246, 0.15); color: #93c5fd; }
.ca-empty {
  padding: 32px 16px;
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
</style>
